import { ChartType, PreviewSlide, WidgetId } from './preview.models';

export interface TextEditOverride {
	type: 'text';
	widgetId: WidgetId;
	text: string;
	/** The reader typed `text` — see `PreviewWidget.editedByUser`. */
	editedByUser?: boolean;
}

export interface ColorEditOverride {
	type: 'color';
	widgetId: WidgetId;
	fromColor: string;
	toColor: string;
}

export interface ChartTypeEditOverride {
	type: 'chart_type';
	widgetId: WidgetId;
	chartType: ChartType;
}

export interface VisibilityEditOverride {
	type: 'visibility';
	widgetId: WidgetId;
	visible: boolean;
}

export interface ExecutiveSummaryMetricsEditOverride {
	type: 'executive_summary_metrics';
	selectedMetricIds: string[];
}

export type SlideEditOverride =
	| TextEditOverride
	| ColorEditOverride
	| ChartTypeEditOverride
	| VisibilityEditOverride
	| ExecutiveSummaryMetricsEditOverride;

const COLOR_PATTERN = /(#[0-9a-f]{3,8}\b|rgba?\([^)]+\))/gi;

/**
 * Repaint one widget, from one colour to another, in place.
 *
 * Pulled out of `applyOverrides` so the slides that build their widgets
 * without going through a `PreviewSlide` — the charts a reader adds — can be
 * themed by the same rule as the fixed ones, rather than by a second
 * implementation that drifts from this one.
 */
export function recolorWidgetInPlace(
	widget: unknown,
	fromColor: string,
	toColor: string
): void {
	replaceLinkedSeriesColorInWidget(widget as any, fromColor, toColor);
	replaceColorInValue(widget, fromColor, toColor);
}

interface ParsedColor {
	r: number;
	g: number;
	b: number;
	alpha?: string;
}

export interface AiEditSuccessResponse {
	status: 'ok';
	overrides: SlideEditOverride[];
	message?: string | null;
}

export interface AiEditUnsupportedResponse {
	status: 'unsupported';
	message: string;
}

export type AiEditResponse = AiEditSuccessResponse | AiEditUnsupportedResponse;

/** An edit the slide-edit agent made to a built slide, ready to be kept. */
export interface AiEditResult {
	originalSlide: PreviewSlide;
	response: AiEditSuccessResponse;
}

export interface ValidationResult {
	valid: boolean;
	errors: string[];
}

export function validateOverrides(
	overrides: SlideEditOverride[],
	slide: PreviewSlide
): ValidationResult {
	const errors: string[] = [];
	const widgetIds = new Set(slide.widgets.map(w => w.id));
	const allowedChartTypes = new Set(slide.allowedChartTypes ?? []);

	for (const override of overrides) {
		if (override.type !== 'executive_summary_metrics') {
			if (!widgetIds.has(override.widgetId)) {
				errors.push(`Widget not found: ${override.widgetId}`);
			}
		}

		if (override.type === 'chart_type') {
			if (
				allowedChartTypes.size > 0 &&
				!allowedChartTypes.has(override.chartType)
			) {
				errors.push(
					`Chart type not allowed for this slide: ${override.chartType}`
				);
			}
		}

		if (override.type === 'executive_summary_metrics') {
			if (slide.slideType !== 'executive_summary') {
				errors.push(
					'Executive summary metrics can only be changed on Executive Summary slide'
				);
			}

			if (!override.selectedMetricIds.length) {
				errors.push(
					'At least one Executive Summary metric is required'
				);
			}
		}

		if (override.type === 'color') {
			if (!isSupportedSourceColorValue(override.fromColor)) {
				errors.push(`Invalid source color: ${override.fromColor}`);
			}

			if (!isSupportedTargetColorValue(override.toColor)) {
				errors.push(`Invalid target color: ${override.toColor}`);
			}

			const widget = slide.widgets.find(
				item => item.id === override.widgetId
			);
			const sourceColors = new Set(
				extractColorsFromValue(widget).map(normalizeColor)
			);

			if (
				isSupportedSourceColorValue(override.fromColor) &&
				!sourceColors.has(normalizeColor(override.fromColor))
			) {
				errors.push(
					`Source color not found in widget: ${override.fromColor}`
				);
			}
		}
	}

	return { valid: errors.length === 0, errors };
}

export function applyOverrides(
	slide: PreviewSlide,
	overrides: SlideEditOverride[]
): PreviewSlide {
	const result: PreviewSlide = JSON.parse(JSON.stringify(slide));

	for (const override of overrides) {
		switch (override.type) {
			case 'text': {
				const widget = result.widgets.find(
					w => w.id === override.widgetId
				);
				if (
					widget &&
					'content' in widget &&
					typeof (widget as any).content?.text === 'string'
				) {
					(widget as any).content.text = override.text;
					// Who wrote the text travels with it: the reader's typing
					// marks the widget, and a rewrite by Ana clears the mark.
					if (override.editedByUser) {
						widget.editedByUser = true;
					} else {
						delete widget.editedByUser;
					}
					if (widget.kind === 'title') {
						result.title = override.text;
					}
					if (widget.kind === 'summary_text') {
						result.description = override.text;
					}
				}
				break;
			}
			case 'chart_type': {
				const widget = result.widgets.find(
					w => w.id === override.widgetId
				);
				if (widget?.kind === 'chart') {
					(widget as any).content.chartType = override.chartType;
					(widget as any).content.chartJsConfig = {
						...(widget as any).content.chartJsConfig,
						type:
							override.chartType === 'area'
								? 'line'
								: override.chartType,
						data: {
							...(widget as any).content.chartJsConfig.data,
							datasets: (
								(widget as any).content.chartJsConfig
									.data as any
							).datasets?.map((d: any) => ({
								...d,
								fill: override.chartType === 'area'
							}))
						}
					};
				}
				break;
			}
			case 'visibility': {
				const widget = result.widgets.find(
					w => w.id === override.widgetId
				);
				if (widget) {
					widget.visible = override.visible;
				}
				if (!result.overrides) result.overrides = {};
				const hidden = new Set(result.overrides.hiddenWidgetIds ?? []);
				if (!override.visible) {
					hidden.add(override.widgetId);
				} else {
					hidden.delete(override.widgetId);
				}
				result.overrides.hiddenWidgetIds = Array.from(hidden);
				break;
			}
			case 'executive_summary_metrics':
				break;
			case 'color': {
				const widget = result.widgets.find(
					w => w.id === override.widgetId
				);
				if (widget) {
					recolorWidgetInPlace(
						widget,
						override.fromColor,
						override.toColor
					);
				}
				break;
			}
		}
	}

	return result;
}

function replaceLinkedSeriesColor(
	slide: PreviewSlide,
	fromColor: string,
	toColor: string
): void {
	for (const widget of slide.widgets) {
		replaceLinkedSeriesColorInWidget(widget, fromColor, toColor);
	}
}

function replaceLinkedSeriesColorInWidget(
	widget: PreviewSlide['widgets'][number],
	fromColor: string,
	toColor: string
): void {
	const normalizedFrom = normalizeColor(fromColor);
	const parsedFrom = parseColor(fromColor);
	const parsedTo = parseColor(toColor);

	if (widget.kind !== 'chart') {
		return;
	}

	const content = (widget as any).content;
	const seriesMeta = content?.seriesMeta as
		| Array<{ label?: string; color?: string }>
		| undefined;
	const chartConfig = content?.chartJsConfig as any;
	if (!seriesMeta?.length || !chartConfig?.data) {
		return;
	}

	seriesMeta.forEach((series, seriesIndex) => {
		if (
			!series.color ||
			!isColorMatch(series.color, normalizedFrom, parsedFrom)
		) {
			return;
		}

		series.color = toColor;
		replaceDatasetColorForSeries(
			chartConfig.data,
			series.label,
			seriesIndex,
			fromColor,
			toColor,
			parsedFrom,
			parsedTo
		);
	});
}

function replaceDatasetColorForSeries(
	data: any,
	seriesLabel: string | undefined,
	seriesIndex: number,
	fromColor: string,
	toColor: string,
	parsedFrom: ParsedColor | null,
	parsedTo: ParsedColor | null
): void {
	const datasets = data.datasets as any[] | undefined;
	if (!datasets?.length) {
		return;
	}

	const matchingDataset = datasets.find(
		dataset => dataset?.label === seriesLabel
	);

	if (matchingDataset) {
		replaceDatasetColorFields(
			matchingDataset,
			fromColor,
			toColor,
			parsedFrom,
			parsedTo
		);
	}

	for (const dataset of datasets) {
		replaceArrayColorAtIndex(
			dataset?.backgroundColor,
			seriesIndex,
			fromColor,
			toColor,
			parsedFrom,
			parsedTo
		);
		replaceArrayColorAtIndex(
			dataset?.borderColor,
			seriesIndex,
			fromColor,
			toColor,
			parsedFrom,
			parsedTo
		);
		replaceArrayColorAtIndex(
			dataset?.pointHoverBackgroundColor,
			seriesIndex,
			fromColor,
			toColor,
			parsedFrom,
			parsedTo
		);
	}
}

function replaceDatasetColorFields(
	dataset: any,
	fromColor: string,
	toColor: string,
	parsedFrom: ParsedColor | null,
	parsedTo: ParsedColor | null
): void {
	['borderColor', 'backgroundColor', 'pointHoverBackgroundColor'].forEach(
		field => {
			if (typeof dataset[field] !== 'string') {
				return;
			}

			dataset[field] = buildReplacementColor(
				dataset[field],
				fromColor,
				toColor,
				parsedFrom,
				parsedTo
			);
		}
	);
}

function replaceArrayColorAtIndex(
	value: unknown,
	index: number,
	fromColor: string,
	toColor: string,
	parsedFrom: ParsedColor | null,
	parsedTo: ParsedColor | null
): void {
	if (!Array.isArray(value) || typeof value[index] !== 'string') {
		return;
	}

	value[index] = buildReplacementColor(
		value[index],
		fromColor,
		toColor,
		parsedFrom,
		parsedTo
	);
}

function extractColorsFromValue(value: unknown): string[] {
	const colors: string[] = [];

	visitValue(value, item => {
		if (typeof item !== 'string') {
			return;
		}

		const matches = item.match(COLOR_PATTERN);
		if (matches) {
			colors.push(...matches);
		}
	});

	return colors;
}

function replaceColorInValue(
	value: unknown,
	fromColor: string,
	toColor: string
): void {
	const normalizedFrom = normalizeColor(fromColor);
	const parsedFrom = parseColor(fromColor);
	const parsedTo = parseColor(toColor);

	visitValue(value, (item, key, parent) => {
		if (
			typeof item !== 'string' ||
			!parent ||
			key === null ||
			!isColorMatch(item, normalizedFrom, parsedFrom)
		) {
			return;
		}

		parent[key] = buildReplacementColor(
			item,
			fromColor,
			toColor,
			parsedFrom,
			parsedTo
		);
	});
}

function visitValue(
	value: unknown,
	visitor: (
		item: unknown,
		key: string | number | null,
		parent: Record<string, unknown> | unknown[] | null
	) => void,
	key: string | number | null = null,
	parent: Record<string, unknown> | unknown[] | null = null
): void {
	visitor(value, key, parent);

	if (Array.isArray(value)) {
		value.forEach((item, index) => visitValue(item, visitor, index, value));
		return;
	}

	if (!value || typeof value !== 'object') {
		return;
	}

	Object.entries(value as Record<string, unknown>).forEach(
		([itemKey, item]) =>
			visitValue(item, visitor, itemKey, value as Record<string, unknown>)
	);
}

function isSupportedSourceColorValue(value: string): boolean {
	return value.length <= 80 && value.match(COLOR_PATTERN)?.[0] === value;
}

function isSupportedTargetColorValue(value: string): boolean {
	return isSupportedSourceColorValue(value);
}

function isColorMatch(
	value: string,
	normalizedFrom: string,
	parsedFrom: ParsedColor | null
): boolean {
	const normalizedValue = normalizeColor(value);

	if (
		normalizedValue === normalizedFrom ||
		(normalizedFrom.startsWith('#') &&
			normalizedValue.startsWith(normalizedFrom) &&
			(normalizedValue.length === 7 || normalizedValue.length === 9))
	) {
		return true;
	}

	const parsedValue = parseColor(value);

	return (
		Boolean(parsedFrom) &&
		Boolean(parsedValue) &&
		parsedValue!.r === parsedFrom!.r &&
		parsedValue!.g === parsedFrom!.g &&
		parsedValue!.b === parsedFrom!.b
	);
}

function buildReplacementColor(
	currentValue: string,
	fromColor: string,
	toColor: string,
	parsedFrom: ParsedColor | null,
	parsedTo: ParsedColor | null
): string {
	const normalizedCurrent = normalizeColor(currentValue);
	const normalizedFrom = normalizeColor(fromColor);
	const normalizedTo = normalizeColor(toColor);
	const parsedCurrent = parseColor(currentValue);

	if (
		normalizedFrom.startsWith('#') &&
		normalizedTo.startsWith('#') &&
		normalizedCurrent.startsWith(normalizedFrom) &&
		normalizedCurrent.length === 9 &&
		normalizedFrom.length === 7 &&
		normalizedTo.length === 7
	) {
		return `${toColor}${currentValue.slice(7)}`;
	}

	if (parsedFrom && parsedTo && parsedCurrent?.alpha) {
		return `rgba(${parsedTo.r}, ${parsedTo.g}, ${parsedTo.b}, ${parsedCurrent.alpha})`;
	}

	return toColor;
}

function normalizeColor(value: string): string {
	return value.trim().toLowerCase().replace(/\s+/g, '');
}

function parseColor(value: string): ParsedColor | null {
	const normalizedValue = normalizeColor(value);

	if (normalizedValue.startsWith('#')) {
		return parseHexColor(normalizedValue);
	}

	if (normalizedValue.startsWith('rgb')) {
		return parseRgbColor(normalizedValue);
	}

	return null;
}

function parseHexColor(value: string): ParsedColor | null {
	const hex = value.slice(1);

	if (hex.length === 3) {
		return {
			r: parseInt(hex[0] + hex[0], 16),
			g: parseInt(hex[1] + hex[1], 16),
			b: parseInt(hex[2] + hex[2], 16)
		};
	}

	if (hex.length !== 6 && hex.length !== 8) {
		return null;
	}

	const parsed: ParsedColor = {
		r: parseInt(hex.slice(0, 2), 16),
		g: parseInt(hex.slice(2, 4), 16),
		b: parseInt(hex.slice(4, 6), 16)
	};

	if (hex.length === 8) {
		parsed.alpha = String(
			Math.round((parseInt(hex.slice(6, 8), 16) / 255) * 1000) / 1000
		);
	}

	return parsed;
}

function parseRgbColor(value: string): ParsedColor | null {
	const match = /^rgba?\(([^)]+)\)$/.exec(value);
	if (!match) {
		return null;
	}

	const parts = match[1].split(',').map(part => part.trim());
	if (parts.length < 3) {
		return null;
	}

	const parsed: ParsedColor = {
		r: Number(parts[0]),
		g: Number(parts[1]),
		b: Number(parts[2])
	};

	if (
		!Number.isFinite(parsed.r) ||
		!Number.isFinite(parsed.g) ||
		!Number.isFinite(parsed.b)
	) {
		return null;
	}

	if (parts[3] !== undefined) {
		parsed.alpha = parts[3];
	}

	return parsed;
}
